import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { emailRegex, getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import AddParticipantMutation from './AddParticipantMutation';
import EmailInput from '../../../common/ParticipantAttributes/Attributes/EmailInput';
import UI from '../../../common/UI';
import useMutation from '../../../api/useMutation';
import useParticipantExistenceCheck from '../../../common/useParticipantExistenceCheck';
import useProject from '../../useProject';
import useValidation from '../../../common/useValidation';

const addParticipantSchema = (requireName: boolean) => yup.object().shape({
  email: yup.string().required().matches(emailRegex, { name: 'email' }),
  first_name: requireName ? yup.string().required() : yup.string(),
  last_name: requireName ? yup.string().required() : yup.string(),
});

export interface AddContactFormProps {
  onSuccess?: (participantId: string) => void;
}

const AddContactForm = ({ onSuccess }: AddContactFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      email: '',
      first_name: '',
      last_name: '',
    },
  });

  const { participantExists, loading: checkingParticipant } = useParticipantExistenceCheck({
    participant: form.values,
  });

  const [addParticipant, { loading: addingParticipant, error }] = useMutation(AddParticipantMutation, {
    variables: {
      input: {
        project: { id: project.id },
        ...form.values,
      },
    },
    onCompleted: ({ addParticipant: { id } }) => {
      if (!participantExists) {
        notifier.success(t('notifications.contact_added'));
      }

      onSuccess?.(id);
    },
  });

  const loading = checkingParticipant || addingParticipant;

  const { errors, valid } = useValidation({
    schema: addParticipantSchema(!participantExists),
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => addParticipant()}>
      <UI.FormGrid>
        <UI.H4>{t('add_a_contact')}</UI.H4>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <EmailInput
          value={form.values}
          onChange={(value) => form.set((contact) => ({ ...contact, ...value }))}
          onBlur={(key) => form.setTouched(key, true)}
          touched={form.touched}
          errors={errors}
          required
          autoFocus
        />

        {!participantExists && (
          <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
            <UI.InputGroup sc={{ valid: !errors.first_name, touched: form.touched.first_name }}>
              <UI.InputLabel htmlFor="FirstName">
                {t('first_name')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.DebouncedInput {...form.text('first_name')} maxLength={100} id="FirstName" />
              <UI.ErrorMessages attribute={t('first_name')} errors={errors.first_name} />
            </UI.InputGroup>
            <UI.InputGroup sc={{ valid: !errors.first_name, touched: form.touched.first_name }}>
              <UI.InputLabel htmlFor="LastName">
                {t('last_name')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.DebouncedInput {...form.text('last_name')} maxLength={100} id="LastName" />
              <UI.ErrorMessages attribute={t('last_name')} errors={errors.last_name} />
            </UI.InputGroup>
          </UI.FormGrid>
        )}

        {participantExists && (
          <UI.Info>
            {t('add_contact_email_exists')}
          </UI.Info>
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={participantExists ? t('continue') : t('add_contact')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default AddContactForm;
